import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import {
  Card,
  Separator,
  Icon,
  Text,
  Link,
  Image,
  Button,
  CardColors,
} from '@/ui'
import { useFetchResponseWithUtils, useReselector } from '@/utils/sharedHooks'
import {
  getCurrentUser,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import { PayrollSetup } from '@/reducers/auth/userReducer'
import { describeBaseAndPerPersonCost } from '../../payrollPrice.copy'
import { usePayrollPrices } from '../../payrollPrice.hooks'
import {
  getMostRecentPayrollInterestSurvey,
  PayrollInterestSurvey,
} from '../payrollInterestSurvey.actions'
import { TextColors, TypographyStyles } from '@/styles'
import { CircularWrapper } from '@/componentsV1/BaseComponents'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'

export const PayrollInterestOutroContent = ({
  latestPayrollSurvey,
}: {
  latestPayrollSurvey?: PayrollInterestSurvey | null
}) => {
  const user = useReselector(getCurrentUser)
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const hasFreePayrollWithSubscription = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payrollIncludedForFreeForSelf
  )
  const payrollPrices = usePayrollPrices()
  const baseAndPerPersonCost = describeBaseAndPerPersonCost(payrollPrices)
  const additionalEmployees = payrollPrices.seat
    ? `Additional employees cost ${payrollPrices.seat}/mo per employee.`
    : 'Additional employees are billed monthly per employee.'

  // todo remove user portion when this is removed from user
  const interestedInGepAt =
    latestPayrollSurvey?.interestedInGepAt || user?.interestedInGepAt
  const payrollSetup = latestPayrollSurvey?.payrollSetup || user?.payrollSetup

  const cardContent = useMemo(() => {
    if (interestedInGepAt) {
      return (
        <Text variant={TypographyStyles.BODY_MD}>
          Our team will review your information to ensure you meet the
          requirements for Heard payroll. You should expect to receive a message
          in about 1 business day.
          <br />
          <br />
          After you’re approved, follow the instructions below to enroll. We’ll
          also send you this information in a message.
        </Text>
      )
    }

    if (
      payrollSetup === PayrollSetup.gusto ||
      payrollSetup === PayrollSetup.other
    ) {
      return (
        <Text variant={TypographyStyles.BODY_MD}>
          Since you’re using a separate payroll provider, your payroll data
          won’t be available in Heard. Please connect with your provider’s
          support team for any further assistance.
        </Text>
      )
    } else if (payrollSetup === PayrollSetup.none) {
      if (isScorp) {
        return (
          <Text variant={TypographyStyles.BODY_MD}>
            Please note that as an S corporation, you’re <b>required</b> by the
            IRS to pay yourself through a payroll.
            <br />
            We’ll remind you to update this information later. If you wish to
            sign up for Heard Payroll in the future, you can find it in your
            sidebar.
          </Text>
        )
      }
      return (
        <Text variant={TypographyStyles.BODY_MD}>
          If your needs change and you wish to sign up for Heard Payroll in the
          future, reach out to us via Messages.
        </Text>
      )
    }
    return null
  }, [interestedInGepAt, isScorp, payrollSetup])

  if (latestPayrollSurvey?.gepEligible === false) {
    return (
      <>
        <Text variant={TypographyStyles.H1}>Heard Payroll</Text>

        <Card color={CardColors.PRIORITY}>
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/plant-snake-short.svg"
            className="w-[72px] h-[80px] bg-transparent"
            alt="Plant Snake Short"
          />
          <Text variant={TypographyStyles.H2} className="mt-4">
            We’re still growing
          </Text>
          <div className="mt-4 space-y-4">
            <Text>
              Our team has determined that we&apos;re unable to currently
              support you via Heard Payroll. Please review Messages for more
              details.
            </Text>
            <Text>
              We&apos;ll be in touch if we&apos;re able to support your needs in
              the future.
            </Text>
            <Text>
              If your circumstances change, and you&apos;d like to be
              re-evaluated, reach out to Support via Messages.
            </Text>
          </div>
        </Card>
      </>
    )
  }

  return (
    <>
      <Text variant={TypographyStyles.H1}>
        Tell us about your payroll needs
      </Text>

      <Card color={CardColors.EMPHASIS} className="text-center p-4">
        <div className=" text-center my-4">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/check.svg"
            className="w-[100px] h-[100px] bg-transparent mx-auto"
            alt="Check"
          />
        </div>
        <Text variant={TypographyStyles.H2} className="text-center">
          {interestedInGepAt
            ? 'Thanks! We’re reviewing your request'
            : 'Thanks for letting us know about your payroll needs!'}
        </Text>
        <div className="mt-4 text-center">{cardContent}</div>
      </Card>

      {interestedInGepAt && (
        <Card>
          <div className="w-full mt-8">
            <Text variant={TypographyStyles.H1}>What to expect next</Text>
          </div>

          {/* Step 1 */}
          <div className="flex flex-col md:flex-row items-start mt-6 gap-4">
            <div className="flex justify-end w-[40px]">
              <CircularWrapper height={28} wrapperColor="stone">
                <Text variant={TypographyStyles.H3}>1</Text>
              </CircularWrapper>
            </div>
            <div className="flex-1 max-w-2/3">
              <Text variant={TypographyStyles.H2}>Enroll in Payroll</Text>
              <Text variant={TypographyStyles.BODY_SM} className="mb-2">
                Once a member of our Support team has verified your eligibility,
                go to <b>Enroll in Payroll</b> in your sidebar.
              </Text>
              {hasFreePayrollWithSubscription ? (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                >
                  Payroll for yourself is included in your plan.{' '}
                  {additionalEmployees}
                </Text>
              ) : (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                >
                  Heard Payroll is {baseAndPerPersonCost}. Once you enroll, your
                  subscription will be charged by Heard, not Gusto.
                </Text>
              )}
            </div>
            <div className="flex justify-center items-center max-w-1/3">
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/payroll_enroll.png"
                className="w-[182px] h-[70px]"
                alt="Payroll Enroll"
              />
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col md:flex-row items-start mt-6 gap-4">
            <div className="flex justify-end w-[40px]">
              <CircularWrapper height={28} wrapperColor="stone">
                <Text variant={TypographyStyles.H3}>2</Text>
              </CircularWrapper>
            </div>
            <div className="flex-1">
              {payrollSetup === PayrollSetup.gusto ? (
                <>
                  <Text variant={TypographyStyles.H2}>Connect to Gusto</Text>
                  <Text variant={TypographyStyles.BODY_SM}>
                    Take a few minutes to link your Heard and Gusto account and
                    accept the Terms of Service.
                  </Text>
                </>
              ) : (
                <>
                  <Text variant={TypographyStyles.H2}>
                    Complete payroll onboarding
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM}>
                    We’ll walk you through the information and documentation
                    needed to ensure compliance with state and federal laws.
                  </Text>
                </>
              )}
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col md:flex-row items-start mt-6 gap-4">
            <div className="flex justify-end w-[40px]">
              <CircularWrapper height={28} wrapperColor="stone">
                <Text variant={TypographyStyles.H3}>3</Text>
              </CircularWrapper>
            </div>
            <div className="flex-1">
              <Text variant={TypographyStyles.H2}>
                Start managing your payroll in Heard
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                Your payroll should now be active in Heard.
                {payrollSetup === PayrollSetup.other &&
                  ' Export your statements from your previous provider and upload them into Gusto.'}
              </Text>

              {payrollSetup === PayrollSetup.gusto && (
                <div className="mt-4">
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                  >
                    <b>Important:</b> Migration fully transfers your payroll to
                    Heard. Your Gusto payroll will no longer be available, nor
                    will they be able to provide support. Please reach out to us
                    via Messages instead.
                  </Text>
                </div>
              )}

              <div className="mt-4">
                <Link
                  href="https://support.joinheard.com/hc/en-us/sections/7709867454359-Payroll"
                  newPage
                  className="text-sm"
                >
                  Heard Payroll help articles{' '}
                  <Icon icon={faArrowRight} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}

export const PayrollInterestOutro = () => {
  const navigate = useNavigate()

  const latestPayrollSurvey = useFetchResponseWithUtils(
    getMostRecentPayrollInterestSurvey
  )

  return (
    <>
      <PayrollInterestOutroContent
        latestPayrollSurvey={latestPayrollSurvey.result}
      />

      <Separator />

      <div className="flex flex-col md:flex-row justify-end items-center gap-4 mt-4">
        {/* Spacer is handled by flex alignment */}
        <Button
          className="w-full md:w-auto md:min-w-[150px]"
          onClick={() => navigate('/')}
        >
          Done
        </Button>
      </div>
    </>
  )
}
